import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AddToCart, RemoveFromCart } from '../../../Redux/actions/cartAction';

const GiftCardModal = ({
  // Add mode
  item,
  onClose,

  // Edit mode
  isEdit = false,
  editingItem,
  closeModal,

  Currency,
}) => {
  const dispatch = useDispatch();

  const baseItem = item || editingItem?.item || editingItem?.itemData || null;

  const initialAmount = isEdit ? editingItem?.price || editingItem?.item?.salePrice || 0 : baseItem?.salePrice || 0;

  const [amount, setAmount] = useState(initialAmount);

  // ✅ NEW: custom buy flag (this is what you asked)
  const [isCustomBuy, setIsCustomBuy] = useState(true);

  useEffect(() => {
    setAmount(initialAmount);
    // keep custom true by default
    setIsCustomBuy(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editingItem?.id, item?.recordId]);

  const safeAmount = useMemo(() => Math.max(0, Number(amount) || 0), [amount]);

  const handleClose = () => {
    if (closeModal) closeModal();
    if (onClose) onClose(false);
  };

  const submit = () => {
    if (safeAmount <= 0) return;

    const displayName = baseItem?.itemDescription || baseItem?.itemName || baseItem?.name || 'Gift Card';

    const id = isEdit
      ? editingItem?.id || `GC-${baseItem?.recordId || baseItem?.itemNo}-${Date.now()}`
      : `GC-${baseItem?.recordId || baseItem?.itemNo}-${Date.now()}`;

    const merged = {
      id,
      name: displayName,
      barcode: baseItem?.barcode,
      quantity: 1,
      price: safeAmount,

      // ✅ flags
      isGiftCard: true,
      isCustomBuy: !!isCustomBuy,
      customBuyAmount: safeAmount,

      // ✅ ADD THIS (important)
      itemData: {
        isGiftCard: true,
        isCustomBuy: !!isCustomBuy,
        customBuyAmount: safeAmount,

        // optional but helpful (if reducer relies on these)
        recordId: baseItem?.recordId,
        itemNo: baseItem?.itemNo || baseItem?.recordId,
        itemName: displayName,
        salePrice: safeAmount,
        barcode: baseItem?.barcode,
      },

      item: {
        recordId: baseItem?.recordId,
        record_Id: baseItem?.recordId,
        itemNo: baseItem?.itemNo || baseItem?.recordId,
        itemName: displayName,
        salePrice: safeAmount,
        barcode: baseItem?.barcode,
        taxRate: 0,
        vat: 0,

        // ✅ for safety
        isGiftCard: true,
        isCustomBuy: !!isCustomBuy,
        customBuyAmount: safeAmount,
      },
    };

    // ✅ If editing -> remove old line then add updated line
    if (isEdit && editingItem) {
      const idx = editingItem?.__index;
      const key = editingItem?.item?.itemNo || editingItem?.item?.recordId;
      if (idx !== undefined && key !== undefined) {
        dispatch(RemoveFromCart(key, idx));
      }
    }

    dispatch(AddToCart(merged));

    if (closeModal) closeModal();
    if (onClose) onClose(true);
  };

  if (!baseItem) return null;

  return (
    <div className="p-4">
      <h5 className="mb-3">{baseItem?.itemDescription || 'Gift Card'}</h5>

      {/* ✅ NEW: Custom Buy toggle */}
      <div className="form-check mb-3">
        <input className="form-check-input" type="checkbox" checked={isCustomBuy} onChange={(e) => setIsCustomBuy(e.target.checked)} id="customBuyCheck" />
        <label className="form-check-label fw-semibold" htmlFor="customBuyCheck">
          Custom Buy
        </label>
      </div>

      <label className="form-label fw-semibold">Gift Card Amount</label>
      <input
        type="number"
        className="form-control"
        value={amount}
        min="0"
        step="0.01"
        onChange={(e) => setAmount(e.target.value)}
        disabled={!isCustomBuy} // ✅ if custom buy is off, user uses presets
      />

      <div className="d-flex gap-2 mt-3 flex-wrap">
        {[25, 50, 100, 200].map((v) => (
          <button
            key={v}
            className="btn btn-outline-secondary"
            onClick={() => {
              setIsCustomBuy(false);
              setAmount(v);
            }}
            type="button"
          >
            {Currency?.currency?.code} {v}
          </button>
        ))}
      </div>

      <div className="d-flex justify-content-end gap-2 mt-4">
        <button className="btn btn-secondary" onClick={handleClose} type="button">
          Cancel
        </button>
        <button className="btn btn-primary" onClick={submit} type="button">
          {isEdit ? 'Update' : 'Add'} - {Currency?.currency?.code} {safeAmount.toFixed(2)}
        </button>
      </div>
    </div>
  );
};

export default GiftCardModal;
